// src/components/PapChatLoader.js
import { useEffect, useRef } from 'react';
import { PAP_CONFIG } from '../utils/papConfig';
import { trackEvent } from '../utils/track';

const SCRIPT_ID = 'pap-chat-sdk-loader';
const SDK_GLOBAL = 'PAP_Chat';

// Bij een fout in de stream stuurt de SDK eerst response_completed en direct
// daarna stream_stopped. We wachten dus even met het geslaagde antwoord, anders
// telt hetzelfde antwoord twee keer: één keer goed en één keer mislukt.
const FOUT_MARGE_MS = 300;

/**
 * Maakt de analytics-callback voor één widget. De SDK roept hem aan bij elk
 * verstuurd bericht en elk afgerond of afgebroken antwoord; wij zetten dat om in
 * ai_message/ai_response, zodat het gebruik van Doortje weer meetbaar is. De
 * berichttekst gaat bewust niet mee, alleen de lengte.
 *
 * Elke widget krijgt een eigen exemplaar (de zwevende assistent en de ingebedde
 * chat kunnen tegelijk openstaan), maar de verwijzing blijft per widget gelijk:
 * een nieuwe functie per render zou de widget opnieuw laten laden.
 */
function maakAnalyticsCallback() {
  let geslaagdTimer = null;

  const stuur = (type, payload) => {
    try {
      trackEvent({ type, path: window.location.pathname, payload });
    } catch {
      // tracking mag de chat nooit in de weg zitten
    }
  };

  return (event) => {
    if (!event || typeof event !== 'object') return;

    if (event.type === 'message_sent') {
      stuur('ai_message', {
        length: event.length,
        lang: event.lang,
        hasPageUrl: !!event.hasPageUrl,
      });
      return;
    }

    if (event.type === 'response_completed') {
      const durationMs = event.durationMs;
      clearTimeout(geslaagdTimer);
      geslaagdTimer = setTimeout(() => {
        geslaagdTimer = null;
        stuur('ai_response', { ok: true, durationMs });
      }, FOUT_MARGE_MS);
      return;
    }

    if (event.type === 'stream_stopped') {
      clearTimeout(geslaagdTimer);
      geslaagdTimer = null;
      stuur('ai_response', { ok: false });
    }
  };
}

const PapChatLoader = ({ 
  mode = 'floating', 
  elementId = null, 
  assistantId = 'doorloopjes',
  initialState = undefined, 
  minimizeAction = 'minimize',
  context = undefined,
  storageMode = 'localStorage', 
  getConversationId,
  onConversationIdUpdate,
  onConversationReset
}) => {
  const isInitialized = useRef(false);
  const containerId = useRef(elementId || `pap-chat-container-${Math.random().toString(36).substr(2, 9)}`);
  // Vaste verwijzing: hoort daarom niet in de deps van het effect hieronder.
  const onAnalytics = useRef(null);
  if (!onAnalytics.current) onAnalytics.current = maakAnalyticsCallback();

  useEffect(() => {
    const CONFIG = PAP_CONFIG;

    const performCleanup = () => {
      if (isInitialized.current && window[SDK_GLOBAL]) {
        const targetEl = document.getElementById(containerId.current);
        if (targetEl) {
          try {
            window[SDK_GLOBAL].destroy(targetEl);
          } catch (e) {
            console.warn('PAP Cleanup warning:', e);
          }
          if (mode === 'floating') {
            targetEl.remove();
          }
        }
      }
      isInitialized.current = false;
    };

    const initWidget = () => {
      if (!window[SDK_GLOBAL] || typeof window[SDK_GLOBAL].init !== 'function') {
        return;
      }

      let targetEl = document.getElementById(containerId.current);
      
      if (!targetEl && mode === 'floating') {
        targetEl = document.createElement('div');
        targetEl.id = containerId.current;
        targetEl.style.zIndex = '999999'; 
        document.body.appendChild(targetEl);
      }

      if (!targetEl) {
        console.error(`[PapChatLoader] Target element #${containerId.current} not found.`);
        return;
      }

      try {
        window[SDK_GLOBAL].init({
          element: targetEl,
          mountType: mode,
          assistantId: assistantId,
          controlPlaneUrl: CONFIG.controlPlaneUrl,
          runtimeEngineUrl: CONFIG.runtimeEngineUrl,
          assetBaseUrl: CONFIG.assetBaseUrl,
          defaultWindowState: initialState,
          minimizeAction: minimizeAction,
          context: context,
          storageMode: storageMode,
          getConversationId: getConversationId,
          onConversationIdUpdate: onConversationIdUpdate,
          onConversationReset: onConversationReset,
          onAnalytics: onAnalytics.current
        });
        isInitialized.current = true;
      } catch (err) {
        console.error("[PapChatLoader] SDK Init failed:", err);
      }
    };

    if (window[SDK_GLOBAL]) {
      setTimeout(initWidget, 0);
    } else {
      let script = document.getElementById(SCRIPT_ID);
      if (!script) {
        script = document.createElement('script');
        script.id = SCRIPT_ID;
        script.src = CONFIG.sdkUrl;
        script.async = true;
        document.body.appendChild(script);
      }

      const onScriptLoad = () => setTimeout(initWidget, 50);
      script.addEventListener('load', onScriptLoad);
      
      return () => {
        script.removeEventListener('load', onScriptLoad);
        performCleanup();
      };
    }

    return performCleanup;
  }, [mode, assistantId, initialState, minimizeAction, context, storageMode, getConversationId, onConversationIdUpdate, onConversationReset]); 

  return null;
};

export default PapChatLoader;